import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { Download, Trash2, Eye } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const AdminEventBrackets = () => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [brackets, setBrackets] = useState([]);
  const [expandedBracket, setExpandedBracket] = useState(null);
  const [loading, setLoading] = useState(true);

  const { toast } = useToast();

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await apiService.admin.getEvents();
      setEvents(response.data || []);
      setLoading(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal memuat event', variant: 'destructive' });
      setLoading(false);
    }
  };

  const handleSelectEvent = async (eventId) => {
    setSelectedEventId(eventId);
    const event = events.find(e => e.id === eventId);
    setSelectedEvent(event);
    setExpandedBracket(null);
    
    try {
      const response = await apiService.admin.getEventBrackets(eventId);
      setBrackets(response.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal memuat bracket', variant: 'destructive' });
      setBrackets([]);
    }
  };

  const handleDeleteBracket = async (bracketId) => {
    if (!window.confirm('Hapus bracket ini?')) return;

    try {
      await apiService.admin.deleteEventBracket(selectedEventId, bracketId);
      toast({ title: 'Berhasil', description: 'Bracket berhasil dihapus' });
      // Reload brackets
      handleSelectEvent(selectedEventId);
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal menghapus bracket', variant: 'destructive' });
    }
  };

  const handleExportBracket = async (bracketId) => {
    try {
      const response = await apiService.admin.exportEventBracketCsv(selectedEventId, bracketId);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bracket_${bracketId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'Berhasil', description: 'Bracket berhasil diekspor' });
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal export bracket', variant: 'destructive' });
    }
  };

  const getTeamName = (teamId) => {
    // Team names are embedded in brackets, so we don't need external lookup
    return teamId;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse">Memuat...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 p-4 sm:p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Hasil Drawing Bracket</h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">Lihat dan kelola drawing bracket yang sudah disimpan</p>
        </div>

        {/* Event Selection */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-lg font-semibold mb-4">Pilih Event</h2>
          <select
            value={selectedEventId}
            onChange={(e) => handleSelectEvent(e.target.value)}
            className="w-full md:w-1/2 px-3 py-2 border rounded-lg"
          >
            <option value="">-- Pilih Event --</option>
            {events
              .filter(e => e.format === 'tournament_elimination')
              .map(event => (
                <option key={event.id} value={event.id}>{event.name}</option>
              ))}
          </select>
        </div>

        {/* Brackets List */}
        {selectedEventId && (
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h2 className="text-lg font-semibold mb-4">Bracket Tersimpan</h2>
            
            {brackets.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Belum ada bracket yang disimpan untuk event ini</p>
            ) : (
              <div className="space-y-4">
                {brackets.map(bracket => (
                  <div key={bracket.id} className="border rounded-lg overflow-hidden">
                    {/* Bracket Header */}
                    <div
                      className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 flex items-center justify-between"
                      onClick={() => setExpandedBracket(expandedBracket === bracket.id ? null : bracket.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
                          <span className="text-sm font-semibold text-blue-600">{bracket.bracket_size}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{bracket.category}</p>
                          <p className="text-sm text-gray-600">
                            {bracket.registered_team_count} tim • {bracket.draw_mode === 'manual' ? 'Manual' : 'Otomatis'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportBracket(bracket.id);
                          }}
                          className="p-2 text-green-600 hover:bg-green-50 rounded"
                          title="Download CSV"
                        >
                          <Download size={18} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBracket(bracket.id);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Hapus"
                        >
                          <Trash2 size={18} />
                        </button>
                        <Eye size={18} className="text-gray-400" />
                      </div>
                    </div>

                    {/* Bracket Details */}
                    {expandedBracket === bracket.id && (
                      <div className="p-4 bg-white border-t">
                        {/* Teams List */}
                        <div className="mb-4">
                          <h3 className="font-semibold text-sm text-gray-900 mb-2">Tim Terdaftar:</h3>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {bracket.teams && bracket.teams.map((team, idx) => (
                              <div key={idx} className="px-2 py-1 bg-blue-50 border border-blue-200 rounded text-sm text-gray-800">
                                {team.team_name}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Bracket Rounds */}
                        <div>
                          <h3 className="font-semibold text-sm text-gray-900 mb-2">Struktur Bracket:</h3>
                          <div className="space-y-2">
                            {bracket.rounds && bracket.rounds.map((round, roundIdx) => (
                              <div key={roundIdx} className="bg-gray-50 p-3 rounded">
                                <p className="font-medium text-sm text-gray-900 mb-1">{round.name}</p>
                                <div className="text-xs text-gray-600 space-y-1">
                                  {round.matches && round.matches.map((match, matchIdx) => (
                                    <div key={matchIdx} className="flex justify-between">
                                      <span>{match.team_a}</span>
                                      <span className="text-gray-400">vs</span>
                                      <span>{match.team_b}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEventBrackets;
